import * as React from "react";
import { useEffect } from "react";

import ReactDOM from "react-dom";
import { FileText, LogOut, Users, DollarSign, ShieldAlert, ClipboardList, MapPin } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Box from "@mui/material/Box";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import { useTheme } from "@emotion/react";
import Toolbar from "@mui/material/Toolbar";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import { styled } from "@mui/material/styles";
import ListItem from "@mui/material/ListItem";
import MenuIcon from "@mui/icons-material/Menu";
import IconButton from "@mui/material/IconButton";
import CssBaseline from "@mui/material/CssBaseline";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { Backdrop, Divider, useMediaQuery } from "@mui/material";
import ListItemButton from "@mui/material/ListItemButton";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

import CallUsNow from "../../common/CallUsNow";
import pusher from "../../../notifications/Pusher";
import LogoIcon from "../../../assets/icons/ep-icon-blue.png";
import NotificationMenu from "../../layout/notifications";
import Facebook_Icon from "../../../assets/articles/facebook.svg";
import MenuIconImg from "../../../assets/dashboard/menu_open.svg";
import SidebarLogo from "../../../assets/footer/equity black.png";
import CookieConsentBanner from "../../common/CookieConsentBanner";
import CallPilotChatButton from "../../common/CallPilotChatButton";
import Instagram_Icon from "../../../assets/articles/instagram.svg";
import { ServicerEpAwareDashboardButton } from "../../layout/ServicerEpAwareDashboardButton";
import { ensureCallPilotChatbotReady } from "../../helpers/callPilotChatbot";
import {
  clearLocalStoarge,
  getObjLength,
  isIosApp,
  redirectToLogin,
  servicerExitLoginUrl,
} from "../../helpers/utils";
import { Button } from "../../common/customDesignedComponents/button";
import { userProfileAction } from "../../../redux/slices/user/userProfileSlice";
import { notificationActions } from "../../../redux/slices/notification/notificationSlice";
import {
  FACEBOOK_LINK,
  INSTAGRAM_LINK,
} from "../../helpers/constants";
import { serviceProviderSignInAction } from "../../../redux/slices/serviceProvider/SPSignINSlice";
import { serviceProviderProfileAction } from "../../../redux/slices/serviceProvider/servicerProfile/servicerProfileSlice";
import { cn } from "../../../lib/utils";
import ServicerDashboardLayout from "../../layout/ServicerDashboardLayout";
import { fetchFulfillmentPendingCount } from "../../for-service-provider/servicerFulfillmentApi";

import "../../../styles/appbar.scss";
import "../../../styles/sidebar.scss";

const JESSE_REDACT_ENABLED =
  String(process.env.REACT_APP_JESSE_REDACT_ENABLED || "").toLowerCase() === "true";

const drawerWidth = 203;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  boxShadow: "0px 0px 2px rgba(52, 75, 91, 0.3)",
  border: "none",
  zIndex: "9",
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  boxShadow: "0px 0px 2px rgba(52, 75, 91, 0.3)",
  border: "none",
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("md")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  [theme.breakpoints.down("md")]: {
    width: "0",
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  padding: theme.spacing(0, 3),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  marginLeft: theme.spacing(7),
  width: `calc(100% - ${theme.spacing(8)} - 1.5px)`,
  [theme.breakpoints.down("md")]: {
    width: "100%",
    marginLeft: "0",
  },
  backgroundColor: "#FFFFFF",
  boxShadow: "0px 0px 2px rgba(52, 75, 91, 0.3)",
  // zIndex: theme.zIndex.drawer + 1,
  zIndex: "9",
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    [theme.breakpoints.down("md")]: {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
    },
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

export default function ServicerSideBar({ comp, isTailwindComponent = false }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isTab = useMediaQuery(theme.breakpoints.down(992));

  const [open, setOpen] = React.useState(true);
  const [openAlert, setOpenAlert] = React.useState(true);
  const [pusherChannel, setPusherChannel] = React.useState(null);
  const [isBelow510, setIsBelow510] = React.useState(false);
  const [isAnyMuiModalOpen, setIsAnyMuiModalOpen] = React.useState(false);
  const [fulfillmentNav, setFulfillmentNav] = React.useState({
    enabled: false,
    offeredCount: 0,
    agreementRequired: false,
  });
  const currentPath = window.location.pathname;
  // store data
  const { servicerProfileDetails } = useSelector(
    (state) => state?.servicerProfile,
  );
  const { reRenderCount } = useSelector((state) => state?.userProfile);

  const isActivePath = (path) => currentPath === path;
  const isDocumentsPath =
    currentPath === "/documents" ||
    currentPath === "/request-document" ||
    currentPath === "/search-property";
  const isRiskAssessmentsPath = currentPath === "/risk-assessments";
  const isJesseProtectedPath = currentPath === "/jesse-protected-parties";
  const isClientsPath =
    currentPath === "/clients" || currentPath.startsWith("/clients/");
  const isCommissionsPath = currentPath === "/commissions";
  const isFulfillmentPath =
    currentPath === "/fulfillment-assignments" ||
    currentPath.startsWith("/fulfillment/clients");

  // The Tailwind layout loads its own profile, pending count and Pusher channel.
  useEffect(() => {
    if (isTailwindComponent) return undefined;
    let cancelled = false;
    const loadFulfillmentNav = async () => {
      try {
        const response = await fetchFulfillmentPendingCount();
        if (cancelled || !response?.success) return;
        setFulfillmentNav({
          enabled: response.data?.enabled === true,
          offeredCount: Number(response.data?.offered_count) || 0,
          agreementRequired: response.data?.agreement_required === true,
        });
      } catch {
        if (!cancelled) {
          setFulfillmentNav({
            enabled: false,
            offeredCount: 0,
            agreementRequired: false,
          });
        }
      }
    };
    void loadFulfillmentNav();
    const timer = window.setInterval(loadFulfillmentNav, 120000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [currentPath, isTailwindComponent]);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const update = () => {
      // MUI Dialog/Modal renders within `.MuiModal-root` (Portal to body)
      const modalCount = document.querySelectorAll(".MuiModal-root").length;
      setIsAnyMuiModalOpen(modalCount > 0);
    };

    update();

    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  const handleModalClosing = () => {
    dispatch(userProfileAction.setReRenderCount(reRenderCount + 1));
    if (window.innerWidth < 575) {
      setOpen((prevState) => !prevState);
    }
  };

  const userPreference = (access) => {
    localStorage.setItem("preference", access);
    setOpenAlert(false);
  };

  // handle sign out button
  const handleSignOut = () => {
    dispatch(
      serviceProviderSignInAction.userSignoutData({
        url: "service-provider/logout",
        data: {},
        token: true,
      }),
    );
    dispatch(serviceProviderProfileAction.logoutPasswordRequired());
    dispatch(serviceProviderSignInAction.clearSigninData());
    const iosFlag = isIosApp();
    clearLocalStoarge();
    localStorage.setItem("ivokeNativeMethod", String(iosFlag));
    redirectToLogin(navigate);
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 510px)");
    const update = () => setIsBelow510(!!mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    if (isTailwindComponent) return;
    dispatch(
      serviceProviderProfileAction.getServiceProviderProfile({
        url: "service-provider/profile?source=service_provider",
        data: {},
        token: true,
      }),
    );
  }, [dispatch, isTailwindComponent]);

  useEffect(() => {
    if (
      !isTailwindComponent &&
      servicerProfileDetails?.data?.user_id &&
      pusherChannel === null
    ) {
      const channel = pusher
        .subscribe(`App.User.${servicerProfileDetails?.data?.user_id}`)
        .unbind("equity_protect")
        .bind("equity_protect", (data) => {
          dispatch(
            notificationActions?.appendLiveNotifications({
              ...data,
              read_at: null,
              // created_at: new Date(),
            }),
          );
          dispatch(notificationActions?.setIsNewNotificationArrive(true));
        });

      setPusherChannel(channel);
    }
  }, [
    dispatch,
    isTailwindComponent,
    pusherChannel,
    servicerProfileDetails?.data?.user_id,
  ]);

  useEffect(() => {
    if (
      getObjLength(servicerProfileDetails) &&
      servicerProfileDetails?.success
    ) {
      if (servicerProfileDetails?.data?.status === "inactive") {
        const loginUrl = servicerExitLoginUrl();
        localStorage.clear();
        localStorage.setItem("preference", true);
        window.location.href = loginUrl;
      }
    }
  }, [servicerProfileDetails]);

  useEffect(() => {
    if (!isTailwindComponent) {
      void ensureCallPilotChatbotReady({ hideLauncher: true });
    }
  }, [isTailwindComponent]);

  if (isTailwindComponent) {
    return <ServicerDashboardLayout comp={comp} />;
  }

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          open={open}
          className={open ? "open" : "close"}
        >
          <Toolbar className="toolbar">
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={() => setOpen(!open)}
              edge="start"
            >
              <img
                src={MenuIconImg}
                alt="Menu Icon"
                className="d-none d-md-block"
              />
              <MenuIcon color="action" className="d-block d-md-none" />
            </IconButton>
            <Link
              className="text-white text-center text-decoration-none"
              to="/"
            >
              <img
                src={SidebarLogo}
                alt="Logo"
                className="d-block d-md-none"
                style={{ height: "30px", width: "84px" }}
              />
            </Link>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                ml: "auto",
              }}
            >
              <NotificationMenu isServiceProvider={true} />

              <div className="px-1 px-md-3 tailwind_component">
                {/* <CallUsNow
                  type="help"
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                /> */}
                {/* Sign Out Button */}
                {open && isBelow510 ? (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleSignOut}
                    className="rounded-md h-8 w-8 p-0"
                    aria-label="Sign out"
                    title="Sign out"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleSignOut}
                    className="rounded-md px-6 max-[499px]:px-3 max-[499px]:h-8 max-[499px]:text-xs"
                  >
                    SIGN OUT
                  </Button>
                )}
              </div>
            </Box>
          </Toolbar>
        </AppBar>
        <Drawer
          variant="permanent"
          open={open}
          className={
            open
              ? window.innerWidth < 575
                ? "open position-absolute"
                : "open"
              : window.innerWidth < 575
                ? "close position-absolute"
                : "close"
          }
          sx={{
            zIndex: isAnyMuiModalOpen ? 0 : 999,
            "& .MuiDrawer-paper": {
              zIndex: isAnyMuiModalOpen ? 0 : 999,
            },
          }}
        >
          <DrawerHeader
            sx={{ minHeight: "24px" }}
            className="mt-2 drawer_header"
          >
            <div
              className="sidebarLogoWrapper "
              style={{ width: open ? "80%" : "100%", cursor: "pointer" }}
              // onClick={() => {
              //   navigate("/");
              // }}
            >
              <Link
                className="text-white text-center text-decoration-none"
                to="/"
              >
                <Box sx={{ display: open ? "visible" : "none" }}>
                  <img src={SidebarLogo} alt="Sidebar Logo" />
                </Box>
                <Box sx={{ display: open ? "none" : "visible" }}>
                  <img
                    src={LogoIcon}
                    alt="Logo Icon"
                    style={{ maxHeight: "24px" }}
                  />
                </Box>
              </Link>
            </div>
          </DrawerHeader>
          {isTailwindComponent && (
            <Divider className="my-3" sx={{ borderColor: "00000057" }} />
          )}
          <List
            className={`${isTailwindComponent ? "tailwind_component" : ""} menuItemsList d-flex flex-column`}
            sx={{ flex: "1 0 auto" }}
          >
            {/* <ListItem disablePadding>
              <Divider className="w-100" textAlign="center" sx={{ py: "8px" }}>
                <Typography sx={{ display: open ? "flex" : "none" }}>
                  For Customer Use
                </Typography>
              </Divider>              
            </ListItem> */}

            {isTailwindComponent ? (
              <div className="flex flex-col gap-1 w-full">
                <li className="w-full">
                  <Link
                    to="/documents"
                    onClick={handleModalClosing}
                    className={cn(
                      "flex items-start gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors w-full min-w-0",
                      isDocumentsPath
                        ? "bg-secondary/10 text-secondary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}
                  >
                    <FileText className="h-5 w-5 shrink-0 mt-0.5" />
                    {open ? (
                      <span className="flex-1 min-w-0 whitespace-normal break-words leading-5">
                        Unlock Request(s)
                      </span>
                    ) : null}
                  </Link>
                </li>
                <li className="w-full">
                  <Link
                    to="/risk-assessments"
                    onClick={handleModalClosing}
                    className={cn(
                      "flex items-start gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors w-full min-w-0",
                      isRiskAssessmentsPath
                        ? "bg-secondary/10 text-secondary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}
                  >
                    <ClipboardList className="h-5 w-5 shrink-0 mt-0.5" />
                    {open ? (
                      <span className="flex-1 min-w-0 whitespace-normal break-words leading-5">
                        Risk Assessments
                      </span>
                    ) : null}
                  </Link>
                </li>
                {JESSE_REDACT_ENABLED ? (
                  <li className="w-full">
                    <Link
                      to="/jesse-protected-parties"
                      onClick={handleModalClosing}
                      className={cn(
                        "flex items-start gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors w-full min-w-0",
                        isJesseProtectedPath
                          ? "bg-secondary/10 text-secondary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      )}
                    >
                      <ShieldAlert className="h-5 w-5 shrink-0 mt-0.5" />
                      {open ? (
                        <span className="flex-1 min-w-0 whitespace-normal break-words leading-5">
                          Protected Parties
                        </span>
                      ) : null}
                    </Link>
                  </li>
                ) : null}
                <li className="w-full">
                  <Link
                    to="/clients"
                    onClick={handleModalClosing}
                    className={cn(
                      "flex items-start gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors w-full min-w-0",
                      isClientsPath
                        ? "bg-secondary/10 text-secondary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}
                  >
                    <Users className="h-5 w-5 shrink-0 mt-0.5" />
                    {open ? (
                      <span className="flex-1 min-w-0 whitespace-normal break-words leading-5">
                        Clients
                      </span>
                    ) : null}
                  </Link>
                </li>
                <li className="w-full">
                  <Link
                    to="/commissions"
                    onClick={handleModalClosing}
                    className={cn(
                      "flex items-start gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors w-full min-w-0",
                      isCommissionsPath
                        ? "bg-secondary/10 text-secondary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}
                  >
                    <DollarSign className="h-5 w-5 shrink-0 mt-0.5" />
                    {open ? (
                      <span className="flex-1 min-w-0 whitespace-normal break-words leading-5">
                        Service fee's
                      </span>
                    ) : null}
                  </Link>
                </li>
                <ServicerEpAwareDashboardButton
                  isSidebarOpen={open}
                  onNavigate={handleModalClosing}
                />
              </div>
            ) : (
              <>
                <ListItem disablePadding>
                  <Link
                    className={`text-white text-center text-decoration-none ${
                      isDocumentsPath ? "active" : ""
                    }`}
                    onClick={handleModalClosing}
                    to="/documents"
                  >
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        width: 250,
                        justifyContent: open ? "initial" : "center",
                        px: 2.5,
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 1 : "auto",
                          justifyContent: "center",
                        }}
                      >
                        <DescriptionOutlinedIcon />
                      </ListItemIcon>
                      <ListItemText
                        className={`menu-item`}
                        primary={open ? "Unlock Request(s)" : ""}
                        sx={{ opacity: open ? 1 : 0 }}
                      />
                    </ListItemButton>
                  </Link>
                </ListItem>
                {fulfillmentNav.enabled ? (
                  <ListItem disablePadding>
                    <Link
                      className={`text-white text-center text-decoration-none ${
                        isFulfillmentPath ? "active" : ""
                      }`}
                      onClick={handleModalClosing}
                      to="/fulfillment-assignments"
                      title={
                        fulfillmentNav.agreementRequired
                          ? "Program agreement acknowledgment required"
                          : "Territory"
                      }
                    >
                      <ListItemButton
                        sx={{
                          minHeight: 48,
                          width: 250,
                          justifyContent: open ? "initial" : "center",
                          px: 2.5,
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: open ? 1 : "auto",
                            justifyContent: "center",
                          }}
                        >
                          <MapPin className="h-5 w-5" />
                        </ListItemIcon>
                        <ListItemText
                          className="menu-item"
                          primary={
                            open
                              ? `Territory${
                                  fulfillmentNav.offeredCount
                                    ? ` (${fulfillmentNav.offeredCount})`
                                    : fulfillmentNav.agreementRequired
                                      ? " · Agreement"
                                      : ""
                                }`
                              : ""
                          }
                          sx={{ opacity: open ? 1 : 0 }}
                        />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                ) : null}
                <ListItem disablePadding>
                  <Link
                    className={`text-white text-center text-decoration-none ${
                      window.location.pathname.split("/").includes("risk-assessments")
                        ? "active"
                        : ""
                    }`}
                    onClick={handleModalClosing}
                    to="/risk-assessments"
                  >
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        width: 250,
                        justifyContent: open ? "initial" : "center",
                        px: 2.5,
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 1 : "auto",
                          justifyContent: "center",
                        }}
                      >
                        <SummarizeOutlinedIcon />
                      </ListItemIcon>
                      <ListItemText
                        className="menu-item"
                        primary={open ? "Risk Assessments" : ""}
                        sx={{ opacity: open ? 1 : 0 }}
                      />
                    </ListItemButton>
                  </Link>
                </ListItem>
                {JESSE_REDACT_ENABLED ? (
                  <ListItem disablePadding>
                    <Link
                      className={`text-white text-center text-decoration-none ${
                        window.location.pathname.split("/").includes("jesse-protected-parties")
                          ? "active"
                          : ""
                      }`}
                      onClick={handleModalClosing}
                      to="/jesse-protected-parties"
                    >
                      <ListItemButton
                        sx={{
                          minHeight: 48,
                          width: 250,
                          justifyContent: open ? "initial" : "center",
                          px: 2.5,
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: open ? 1 : "auto",
                            justifyContent: "center",
                          }}
                        >
                          <ShieldAlert className="h-5 w-5" />
                        </ListItemIcon>
                        <ListItemText
                          className="menu-item"
                          primary={open ? "Protected Parties" : ""}
                          sx={{ opacity: open ? 1 : 0 }}
                        />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                ) : null}
                <ListItem disablePadding>
                  <Link
                    className={`text-white text-center text-decoration-none ${
                      window.location.pathname.split("/").includes("clients")
                        ? "active"
                        : ""
                    }`}
                    onClick={handleModalClosing}
                    to="/clients"
                  >
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        width: 250,
                        justifyContent: open ? "initial" : "center",
                        px: 2.5,
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 1 : "auto",
                          justifyContent: "center",
                        }}
                      >
                        <Users className="h-5 w-5" />
                      </ListItemIcon>
                      <ListItemText
                        className="menu-item"
                        primary={open ? "Clients" : ""}
                        sx={{ opacity: open ? 1 : 0 }}
                      />
                    </ListItemButton>
                  </Link>
                </ListItem>
                <ListItem disablePadding>
                  <Link
                    className={`text-white text-center text-decoration-none ${
                      window.location.pathname.split("/").includes("commissions")
                        ? "active"
                        : ""
                    }`}
                    onClick={handleModalClosing}
                    to="/commissions"
                  >
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        width: 250,
                        justifyContent: open ? "initial" : "center",
                        px: 2.5,
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 1 : "auto",
                          justifyContent: "center",
                        }}
                      >
                        <DollarSign className="h-5 w-5" />
                      </ListItemIcon>
                      <ListItemText
                        className="menu-item"
                        primary={open ? "Service fee's" : ""}
                        sx={{ opacity: open ? 1 : 0 }}
                      />
                    </ListItemButton>
                  </Link>
                </ListItem>
              </>
            )}

            {/* <ListItem disablePadding>
              <Divider className="w-100" textAlign="center" sx={{ py: "8px" }}>
                <Typography sx={{ display: open ? "flex" : "none" }}>
                  For Personal Use
                </Typography>
              </Divider>
            </ListItem>
            <ListItem disablePadding>
              <Link
                className={`text-white text-center text-decoration-none ${
                  window.location.pathname
                    .split("/")
                    .includes("servicer-properties")
                    ? "active"
                    : ""
                }`}
                onClick={handleModalClosing}
                to="/servicer-properties"
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    width: 250,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 1 : "auto",
                      justifyContent: "center",
                    }}
                  >
                    <HomeOutlinedIcon sx={{ fontSize: "1.675rem" }} />
                  </ListItemIcon>
                  <ListItemText
                    className={`menu-item`}
                    primary={"Properties"}
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </Link>
            </ListItem>
            <ListItem disablePadding>
              <Link
                className={`text-white text-center text-decoration-none ${
                  window.location.pathname
                    .split("/")
                    .includes("servicer-documents")
                    ? "active"
                    : ""
                }`}
                onClick={handleModalClosing}
                to="/servicer-documents"
              >
                <ListItemButton
                  sx={{
                    width: 250,
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 1 : "auto",
                      justifyContent: "center",
                    }}
                  >
                    <b>
                      <FolderCopyOutlinedIcon />
                    </b>
                  </ListItemIcon>
                  <ListItemText
                    className="menu-item"
                    primary={"Documents"}
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </Link>
            </ListItem>
            <ListItem disablePadding>
              <Link
                className={`text-white text-center text-decoration-none ${
                  window.location.pathname
                    .split("/")
                    .includes("servicer-allow-access")
                    ? "active"
                    : ""
                }`}
                onClick={handleModalClosing}
                to="/servicer-allow-access"
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    width: 250,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 1 : "auto",
                      justifyContent: "center",
                    }}
                  >
                    <SupervisedUserCircleOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText
                    className="menu-item"
                    primary={"Allow Access"}
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </Link>
            </ListItem>
            <ListItem disablePadding>
              <Link
                className={`text-white text-center text-decoration-none ${
                  window.location.pathname.split("/").includes("servicer-logs")
                    ? "active"
                    : ""
                }`}
                onClick={handleModalClosing}
                to="/servicer-logs"
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    width: 250,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 1 : "auto",
                      justifyContent: "center",
                    }}
                  >
                    <ListIcon />
                  </ListItemIcon>
                  <ListItemText
                    className="menu-item"
                    primary={"Logs"}
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </Link>
            </ListItem> */}
          </List>
          {!isTailwindComponent ? (
            <Box
              className="border-top mt-auto shrink-0"
              sx={{ p: open ? 2 : 1 }}
            >
              <CallPilotChatButton variant="subtle" isSidebarOpen={open} />
            </Box>
          ) : null}
          {/* <Stack
            direction={open ? "row" : "column"}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            className="text-center border-top pt-2"
            sx={{}}
          >
            <a
              href={INSTAGRAM_LINK}
              target="_blank"
              rel="noreferrer"
              className="px-2 pt-2"
            >
              <img
                src={Instagram_Icon}
                alt="Facebook"
                style={{ height: "25px" }}
              />
            </a>

            <a
              href={FACEBOOK_LINK}
              target="_blank"
              rel="noreferrer"
              className="px-2 pt-2"
            >
              <img
                src={Facebook_Icon}
                alt="Facebook"
                style={{ height: "25px" }}
              />
            </a>
          </Stack>
          <Box
            className="text-center p-0 pb-3"
            p={3}
            sx={{ display: open ? "visible" : "none" }}
          >
            <CallUsNow
              type="text"
              anchorOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
            />
          </Box>
          <Box
            className="text-center p-0 py-2"
            sx={{ display: open ? "none" : "block", py: 3 }}
          >
            <div className="call cursor-pointer">
              <CallUsNow
                type="icon"
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
              />
            </div>
          </Box> */}
        </Drawer>
        <Box
          component="main"
          className={`main p-4 p-md-5 mt-5 mt-md-0 ${
            isTailwindComponent ? "tailwind_component bg-background min-h-screen" : ""
          }`}
          sx={{ flexGrow: 1, overflowX: "hidden" }}
          key={reRenderCount}
        >
          <DrawerHeader sx={{ minHeight: "35px !important" }} />
          {/* {userProfileDetails?.success ? (
          comp
        ) : (
          <Box className="text-center">
            <span
              className="text-danger text-capitalize"
              // onClick={clearUserData}
            >
              {userProfileDetails?.data?.message ? (
                <>
                  {`${userProfileDetails?.data?.message}`}
                  <Link
                    to={"/login"}
                    className="bold text-decoration-none"
                    onClick={clearUserData}
                  >
                    &nbsp;Click here to Login again.
                  </Link>
                </>
              ) : null}
            </span>
          </Box>
        )} */}
          {comp}
        </Box>

        <CookieConsentBanner
          isVisible={openAlert}
          onAccept={() => userPreference(true)}
          onDecline={() => userPreference(false)}
          userPreferences={
            localStorage.getItem("preference") === null ||
            localStorage.getItem("preference").length === 0
          }
          isTab={isTab}
          code="web"
        />
      </Box>
      {open && window.innerWidth < 575
        ? ReactDOM.createPortal(
            <>
              <Backdrop
                sx={{ zIndex: 995 }}
                open={open}
                onClick={() => {
                  setOpen((prevState) => !prevState);
                }}
              />
            </>,
            document.body,
          )
        : null}
    </>
  );
}
